import {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
} from "react";
import { toast } from "react-toastify";

import LabelImportantIcon from '@material-ui/icons/LabelImportant';
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import { Chip } from "@material-ui/core";

import { emitSocket, getSocket } from "../../services/socket";
import api from "../../services/api";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

import HeaderManager from "../../components/HeaderTableManager/HeaderTableManager";
import TableManager from "../../components/TableManager/TableManager";
import ConfirmationModal from "../../components/ConfirmationModal";
import TagModal from "../../components/TagModal";

import PencilEditIcon from "../../assets/icon/PencilEditIcon";
import TrashIcon from "../../assets/icon/TrashIcon";
import AddIcon from "../../assets/icon/AddIcon";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  tableContainer: {
    width: '100%',
    maxWidth: '80%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '90%',
    },
    [theme.breakpoints.down('md')]: {
      maxWidth: '90%',
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: '90%'
    },
  },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case 'LOAD_TAGS':
      return [...action.payload];
    case 'UPDATE_TAGS':
      const tagIndex = state.findIndex(tag => tag.id === action.payload.id);
      if (tagIndex !== -1) {
        state[tagIndex] = action.payload;
        return [...state];
      } else {
        return [action.payload, ...state];
      }
    case 'DELETE_TAG':
      return state.filter(tag => tag.id !== action.payload);
    case 'RESET':
      return [];
    default:
      return state;
  }
};

const Tags = () => {
  const { user } = useContext(AuthContext);
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);

  const fetchTags = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/tags/", {
        params: { searchParam, pageNumber, kanban: 0 },
      });
      dispatch({ type: "LOAD_TAGS", payload: data });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchTags();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, fetchTags]);

  useEffect(() => {
    const socket = getSocket(user.companyId);

    const handleTagEvent = (data) => {
      if (data.action === "update" && data.tag) {
        dispatch({ type: "UPDATE_TAGS", payload: data.tag });
      }
      
      if (data.action === "create" && data.tag) {
        dispatch({ type: "UPDATE_TAGS", payload: data.tag });
      }

      if (data.action === "delete" && data.tagId) {
        dispatch({ type: "DELETE_TAG", payload: +data.tagId });
      }
    };

    socket.on("user", handleTagEvent);

    return () => {
      socket.off("user", handleTagEvent);
    };
  }, [user]);

  const handleOpenTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(true);
  };

  const handleCloseTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setTagModalOpen(true);
  };

  const handleDeleteTag = async (tagId) => {
    const companyId = localStorage.getItem("companyId");

    try {
      await api.delete(`/tags/${tagId}`);

      // ================================
      // 🔥 EMIT CON SISTEMA CENTRALIZADO
      // ================================
      emitSocket("user", {
        action: "delete",
        tagId,
        companyId,
      });

      dispatch({ type: "DELETE_TAG", payload: tagId });
      toast.success(i18n.t("tags.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }

    setDeletingTag(null);
    setConfirmModalOpen(false);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const primaryButtons = [
    { label: i18n.t("tags.buttons.add"), onClick: handleOpenTagModal, icon: <AddIcon fill='#000' /> },
  ];

  const columns = [
    { 
      field: 'name', 
      headerName: i18n.t("tags.table.name"),
      renderCell: ({ row }) => (
        <Chip
          icon={<LabelImportantIcon style={{ color: row.color }}/>}
          variant="outlined"
          style={{
            border: `2px solid ${row.color}`
          }}
          label={row.name}
        />
      ),
      headerStyle: {fontWeight: '400'}
      ,width: '40%'
    },
    { field: 'ticketCount', headerName: i18n.t("tags.table.tickets"), headerStyle: {fontWeight: '400'},width: '40%' },
  ];

  const actionButtons = [
    ({row}) => (
      <IconButton size="small" onClick={() => handleEditTag(row)}>
        <PencilEditIcon fill="#7F7F7F" />
      </IconButton>
    ),
    ({row}) => (
      <IconButton
        size="small"
        onClick={() => {
          setConfirmModalOpen(true);
          setDeletingTag(row);
        }}
      >
        <TrashIcon fill="#7F7F7F" />
      </IconButton>
    ),
  ];

  return (
    <>
      <div className={classes.mainContainer} >
        <ConfirmationModal
          title={deletingTag && `${i18n.t("tags.confirmationModal.deleteTitle")}`}
          open={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          onConfirm={() => handleDeleteTag(deletingTag.id)}
        >
          {i18n.t("tags.confirmationModal.deleteMessage")}
        </ConfirmationModal>
        <TagModal
          open={tagModalOpen}
          onClose={handleCloseTagModal}
          reload={fetchTags}
          aria-labelledby="form-dialog-title"
          tagId={selectedTag && selectedTag.id}
          kanban={0}
          dispatch={dispatch}
        />
        <div className={classes.tableContainer}>
          <HeaderManager
            title={`${i18n.t("tags.title")}`}
            primaryButtons={primaryButtons}
            onSearch={handleSearch}
          />
          <TableManager
            columns={columns}
            data={tags}
            loading={loading}
            onScroll={loadMore}
            actionButtons={actionButtons}
          />
        </div>
      </div>
    </>
  );
};

export default Tags;